import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Define types for sprint/iteration burndown data
export interface SprintInfo {
  id: string;
  name: string;
  startDate?: string;
  endDate?: string;
}

export interface BurndownDataset {
  labels: string[];
  planned: number[];
  actual: number[];
  sprintInfo: SprintInfo;
}

interface BurndownChartProps {
  burndownDatasets: BurndownDataset[];
  title?: string;
  height?: number;
}

/**
 * Normalizes burndown data to a consistent 0-100% scale for both axes
 * 
 * @param data Array of remaining work values through a sprint
 * @param targetLength Number of points to generate on the x-axis (percentage points)
 * @returns Normalized array where:
 *   - X-axis represents percentage through sprint (0-100%)
 *   - Y-axis represents percentage of work remaining (0-100%)
 */
function normalizeDataToPercentage(data: number[], targetLength: number): number[] {
  // Handle empty data
  if (!data || data.length === 0) return Array(targetLength).fill(0);
  
  // Find the maximum value to use as 100% of work items
  // Typically this would be the first value in a burndown chart
  const maxValue = Math.max(...data);
  
  // Create a normalized array where:
  // - Each index position represents a percentage through the sprint
  // - Each value represents the percentage of work remaining at that point
  const result: number[] = [];
  
  // For each percentage point on the x-axis (0% to 100%)
  for (let i = 0; i < targetLength; i++) {
    // Calculate the percentage through the sprint (0 to 1)
    const percentThroughSprint = i / (targetLength - 1);
    
    // Find the corresponding position in the source data
    // This maps from percentage through sprint to the actual data point index
    const sourcePosition = percentThroughSprint * (data.length - 1);
    const lowerIndex = Math.floor(sourcePosition);
    const upperIndex = Math.min(lowerIndex + 1, data.length - 1);
    
    let value: number;
    
    // If we're exactly on a data point or at the end
    if (lowerIndex === upperIndex || Math.abs(sourcePosition - lowerIndex) < 0.001) {
      value = data[lowerIndex];
    } else {
      // Interpolate between two data points for smoother curves
      const fraction = sourcePosition - lowerIndex;
      value = data[lowerIndex] * (1 - fraction) + data[upperIndex] * fraction;
    }
    
    // Convert the absolute value to a percentage of the maximum
    // This ensures the y-axis is always 0-100% regardless of the total number of work items
    const percentValue = maxValue > 0 ? (value / maxValue) * 100 : 0;
    
    // Ensure the value is within 0-100 range
    result.push(Math.max(0, Math.min(100, percentValue)));
  }
  
  return result;
}

const BurndownChart: React.FC<BurndownChartProps> = ({ 
  burndownDatasets, 
  title = '', 
  height = 350 
}) => {
  // Create standardized percentage labels (0%, 10%, 20%, etc.)
  const percentagePoints = 11; // 0% to 100% in 10% increments
  const percentageLabels = Array.from({ length: percentagePoints }, (_, i) => `${i * 10}%`);
  
  // Define colors for sprints
  const sprintColors = [
    { planned: { borderColor: '#3949ab', backgroundColor: 'rgba(57, 73, 171, 0.1)' }, actual: { borderColor: '#e53935', backgroundColor: 'rgba(229, 57, 53, 0.1)' } },  // Blue/Red (Current)
    { planned: { borderColor: '#43a047', backgroundColor: 'rgba(67, 160, 71, 0.1)' }, actual: { borderColor: '#2e7d32', backgroundColor: 'rgba(46, 125, 50, 0.1)' } },  // Green
    { planned: { borderColor: '#ff9800', backgroundColor: 'rgba(255, 152, 0, 0.1)' }, actual: { borderColor: '#ef6c00', backgroundColor: 'rgba(239, 108, 0, 0.1)' } },  // Orange
    { planned: { borderColor: '#9c27b0', backgroundColor: 'rgba(156, 39, 176, 0.1)' }, actual: { borderColor: '#7b1fa2', backgroundColor: 'rgba(123, 31, 162, 0.1)' } },  // Purple
    { planned: { borderColor: '#00acc1', backgroundColor: 'rgba(0, 172, 193, 0.1)' }, actual: { borderColor: '#0097a7', backgroundColor: 'rgba(0, 151, 167, 0.1)' } },  // Cyan
  ];

  // Prepare chart data for multiple sprints with percentage-based x-axis
  const chartData: ChartData<'line'> = {
    labels: percentageLabels,
    datasets: burndownDatasets.flatMap((sprint: BurndownDataset, index: number) => {
      if (!sprint || !sprint.sprintInfo) return [];
      
      const colorIndex = index % sprintColors.length;
      const datasets = [];
      
      // Add planned line (dashed)
      if (index === 0 && sprint.planned && sprint.planned.length > 0) {
        datasets.push({
          label: `Ideal Burndown`,
          data: normalizeDataToPercentage(sprint.planned, percentagePoints),
          borderColor: sprintColors[colorIndex].planned.borderColor,
          backgroundColor: sprintColors[colorIndex].planned.backgroundColor,
          fill: false,
          borderDash: [5, 5],
          // Hide planned lines for older sprints to reduce clutter
          hidden: index > 0
        });
      }
      
      // Add actual line (solid)
      if (sprint.actual && sprint.actual.length > 0) {
        datasets.push({
          label: `${sprint.sprintInfo.name || 'Sprint'} (Actual)`,
          data: normalizeDataToPercentage(sprint.actual, percentagePoints),
          borderColor: sprintColors[colorIndex].actual.borderColor,
          backgroundColor: sprintColors[colorIndex].actual.backgroundColor,
          fill: false
        });
      }
      
      return datasets;
    })
  };

  // Check if we're in mobile view
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: isMobile ? 15 : 10,
      }
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          boxWidth: isMobile ? 12 : 15,
          boxHeight: isMobile ? 12 : 15,
          padding: isMobile ? 12 : 15,
          font: {
            size: isMobile ? 11 : 12
          },
          // Truncate long labels on mobile
          ...(isMobile && {
            generateLabels: function(chart) {
              const original = ChartJS.defaults.plugins.legend.labels.generateLabels;
              const labels = original.call(this, chart);
              
              return labels.map(label => ({
                ...label,
                text: label.text.length > 20 ? label.text.substring(0, 17) + '...' : label.text
              }));
            }
          })
        },
        // Add spacing below legend
        ...(isMobile && {
          onClick: function(e, legendItem, legend) {
            if (typeof legendItem.datasetIndex === 'number') {
              const index = legendItem.datasetIndex;
              const chart = legend.chart;
              const meta = chart.getDatasetMeta(index);
              const isHidden = meta.hidden === null ? chart.data.datasets[index].hidden : meta.hidden;
              meta.hidden = !isHidden;
              chart.update();
            }
          }
        })
      },
      title: {
        display: title ? true : false,
        text: title,
        font: {
          size: isMobile ? 14 : 16,
          weight: 'bold',
        },
        padding: {
          bottom: isMobile ? 15 : 20
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        titleFont: {
          size: isMobile ? 11 : 12
        },
        bodyFont: {
          size: isMobile ? 10 : 11
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        min: 0,
        max: 100,
        title: {
          display: true,
          text: 'Remaining Work (%)',
        },
        ticks: {
          callback: function(value) {
            return value + '%';
          }
        }
      },
      x: {
        title: {
          display: true,
          text: 'Sprint Time Elapsed',
        }
      }
    },
    elements: {
      line: {
        tension: 0.3,
      },
      point: {
        radius: 3,
        hoverRadius: 6,
      },
    },
  };

  if (!burndownDatasets || burndownDatasets.length === 0) {
    return (
      <div style={{ 
        height: `${height}px`, 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        backgroundColor: '#f5f5f5',
        borderRadius: '8px',
        color: '#666'
      }}>
        No burndown data available
      </div>
    );
  }

  return (
    <div style={{ height: `${height}px` }}>
      <Line options={chartOptions} data={chartData} height={height} />
    </div>
  );
};

export default BurndownChart;
